import { Injectable } from '@angular/core';
import { SlideShowData } from '../interface/slide-show-data';
import { DomainConstants } from '../constants/domain-constants';
import { RuntimeConstants } from '../constants/runtime-constants';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { SalesProductsService } from 'src/app/information-management/sales-products/services/sales-products.service';

@Injectable({
  providedIn: 'root'
})
export class SlideShowDataService {

  constructor(
    private salesProductsService: SalesProductsService,
    private httpClient: HttpClient
  ) {
  }

  getSlideShowDetails(terminalId: number): Observable<Array<SlideShowData>> {
    return this.httpClient.get<Array<any>>(
      `${RuntimeConstants.API_PATH}slideshows/${terminalId}/slideshowdetails`
    ).pipe(
      map((slideShowDetails) => this.mapSlideShowDetails(slideShowDetails))
    );
  }

  mapSlideShowDetails(slideShowDetails: Array<any>): Array<SlideShowData> {
    const rawData: Array<SlideShowData> = [];
    slideShowDetails?.forEach((slideShow) => {
      const imagePath = slideShow.ImagePath?.startsWith('http') ? slideShow.ImagePath : `${RuntimeConstants.IMAGE_BASE_PATH}/${DomainConstants.ImageDirectories.slideImages}/${slideShow.ImagePath}`;
      rawData.push({
        source: imagePath,
        duration: slideShow.HoldSeconds * 1000,
        id: slideShow.Id,
        salesProductId: slideShow.SalesProductId,
        isInStock: slideShow.IsInStock,
        isActive: slideShow.IsActive,
        isAllowOutOfStockOrder: slideShow.IsAllowOutOfStockOrder
      });
    });
    return rawData;
  }

  filterActiveSlides(slideShowRawData: Array<SlideShowData>): Array<SlideShowData> {
    return slideShowRawData ? slideShowRawData.filter(item => item.isActive && (item.isAllowOutOfStockOrder || item.isInStock)) : [];
  }

  updateOutOfStock(salesProductId: number, sizeIds: Array<number>, slideShowRawData: Array<SlideShowData>): Observable<Array<SlideShowData>> {
    if (!slideShowRawData || !slideShowRawData.some(item => item.salesProductId === salesProductId)) {
      return of(slideShowRawData);
    }

    return this.salesProductsService.getSalesProductSizes(salesProductId)
      .pipe(
        map(sizes => {
          sizes.forEach(size => {
            if (sizeIds?.includes(size.SizeId)) {
              size.IsInStock = false;
            }
          });
          const allOutOfStock = sizes.every(size => !size.IsInStock);
          return slideShowRawData.map(item => {
            if (item.salesProductId === salesProductId) {
              return { ...item, isInStock: !allOutOfStock };
            }
            return item;
          });
        })
      );
  }

  updateReplenished(salesProductId: number, slideShowRawData: Array<SlideShowData>): Array<SlideShowData> {
    if (!slideShowRawData) return [];
    let hasChanges = false;
    const updatedRawData = slideShowRawData.map(item => {
      if (item.salesProductId === salesProductId) {
        hasChanges = true;
        return { ...item, isInStock: true };
      }
      return item;
    });

    return hasChanges ? updatedRawData : slideShowRawData;
  }

  updateSalesProduct(salesProductId: number, isActive: boolean, isAllowOutOfStockOrder: boolean, slideShowRawData: Array<SlideShowData>): Array<SlideShowData> {
    if (!slideShowRawData) return [];
    let hasChanges = false;
    const updatedRawData = slideShowRawData.map(item => {
      if (item.salesProductId === salesProductId) {
        hasChanges = true;
        return { ...item, isActive, isAllowOutOfStockOrder };
      }
      return item;
    });

    return hasChanges ? updatedRawData : slideShowRawData;
  }
}
